// CSV パース・stress-strain 変換ユーティリティ

export interface StressStrainPoint {
  strain: number; // ひずみ (%)
  stress: number; // 応力 (MPa)
}

export interface FilamentTestData {
  filamentId: string;
  filamentName: string;
  maker?: string;
  color?: string;
  orientation?: string; // X / XY / Z
  data: StressStrainPoint[];
  metadata: {
    span_mm?: number;
    width_mm?: number;
    thickness_mm?: number;
  };
}

/**
 * CSV一覧インデックスのエントリ型
 */
export interface CSVIndexEntry {
  maker: string;
  type: string;
  color: string;
  orientation: string;
  path: string;
}

/**
 * メーカー優先順位リスト（厳密にこの順）
 */
export const MANUFACTURER_PRIORITY = [
  'ELEGOO',
  'Polymaker',
  'eSUN',
  'TINMORY',
  'PRINSFIL',
  'FLASHFORGE',
  'Creality',
  'SUNLU',
  'OVERTURE',
  'FOXTRUDER',
  'CC3D'
];

/**
 * メーカー優先順位でソート
 */
export function sortByManufacturerPriority<T extends { maker?: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const aMaker = a.maker || '';
    const bMaker = b.maker || '';
    const aIndex = MANUFACTURER_PRIORITY.indexOf(aMaker);
    const bIndex = MANUFACTURER_PRIORITY.indexOf(bMaker);
    
    // 両方リストにある場合は優先順位順
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    
    // aだけリストにある場合はaが優先
    if (aIndex !== -1) return -1;
    
    // bだけリストにある場合はbが優先
    if (bIndex !== -1) return 1;
    
    // 両方リスト外の場合はアルファベット順
    return aMaker.localeCompare(bMaker);
  });
}

/**
 * 優先順位に従って最優先メーカーを選択
 */
export function selectBestManufacturer<T extends { maker?: string }>(items: T[]): T | null {
  if (items.length === 0) return null;
  
  for (const priorityMaker of MANUFACTURER_PRIORITY) {
    const found = items.find(item => item.maker === priorityMaker);
    if (found) return found;
  }
  
  // リスト外の場合はアルファベット順で最初
  return [...items].sort((a, b) => (a.maker || '').localeCompare(b.maker || ''))[0];
}

/**
 * CSVキャッシュ（URLベース）
 */
const csvCache = new Map<string, FilamentTestData | null>();
const csvFetchPromises = new Map<string, Promise<FilamentTestData | null>>();

/**
 * CSVを取得してパース（キャッシュ付き）
 */
async function fetchAndParseCSV(
  csvPath: string,
  filamentType: string,
  filamentName: string
): Promise<FilamentTestData | null> {
  // キャッシュチェック
  if (csvCache.has(csvPath)) {
    return csvCache.get(csvPath)!;
  }

  // 同時fetch防止
  if (csvFetchPromises.has(csvPath)) {
    return csvFetchPromises.get(csvPath)!;
  }

  const fetchPromise = (async () => {
    try {
      const response = await fetch(csvPath);
      if (!response.ok) {
        csvCache.set(csvPath, null);
        return null;
      }
      
      const text = await response.text();
      const parsed = parseStressStrainCSV(text, filamentType, filamentName);
      csvCache.set(csvPath, parsed);
      return parsed;
    } catch (err) {
      console.error(`[CSV] 取得エラー: ${csvPath}`, err);
      csvCache.set(csvPath, null);
      return null;
    } finally {
      csvFetchPromises.delete(csvPath);
    }
  })();

  csvFetchPromises.set(csvPath, fetchPromise);
  return fetchPromise;
}

/**
 * 指定されたtypeとpatternに一致する全メーカーのデータを取得（メーカー比較用）
 */
export async function fetchAllManufacturersData(
  filamentType: string,
  pattern: string
): Promise<FilamentTestData[]> {
  console.log('[fetchAllManufacturersData] 開始:', { filamentType, pattern });

  const csvIndex = await getCSVIndex();
  if (csvIndex.length === 0) {
    console.log('[fetchAllManufacturersData] index.json が空です');
    return [];
  }

  const requestedType = filamentType.trim().toUpperCase();
  const requestedPattern = pattern.toLowerCase();

  // type完全一致 & pattern完全一致の候補を抽出
  const candidates = csvIndex.filter(entry => {
    const parsedMeta = parsePathMetadata(entry.path);
    if (!parsedMeta) return false;
    
    const parsedType = parsedMeta.type.trim().toUpperCase();
    const parsedPattern = parsedMeta.orientation.toLowerCase();
    
    return parsedType === requestedType && parsedPattern === requestedPattern;
  });

  console.log(`[fetchAllManufacturersData] 候補: ${candidates.length} 件`);

  if (candidates.length === 0) {
    return [];
  }

  // メーカーごとにグループ化
  const byMaker = new Map<string, CSVIndexEntry[]>();
  for (const entry of candidates) {
    const parsedMeta = parsePathMetadata(entry.path);
    if (!parsedMeta) continue;
    
    const maker = parsedMeta.maker;
    if (!byMaker.has(maker)) {
      byMaker.set(maker, []);
    }
    byMaker.get(maker)!.push(entry);
  }

  // 各メーカーの代表データを取得（キャッシュ利用）
  const results: FilamentTestData[] = [];
  
  for (const [maker, entries] of byMaker) {
    // 同一メーカーで複数ある場合は色優先度で選択
    const bestEntry = selectBestCSVFromEntries(entries);
    if (!bestEntry) continue;

    const parsed = await fetchAndParseCSV(bestEntry.path, filamentType, filamentType);
    
    if (parsed) {
      // キャッシュオブジェクトを汚染しないようクローンを作成
      const cloned = { ...parsed };
      cloned.maker = maker;
      cloned.color = bestEntry.color;
      cloned.orientation = bestEntry.orientation;
      cloned.filamentName = `${filamentType} (${maker})`;
      cloned.filamentId = maker; // グラフ別キーにするため maker を使用
      results.push(cloned);
    }
  }

  // 優先順位でソート
  return sortByManufacturerPriority(results);
}

/**
 * エントリ配列から色優先度で最適なものを選択
 */
function selectBestCSVFromEntries(entries: CSVIndexEntry[]): CSVIndexEntry | null {
  if (entries.length === 0) return null;
  
  // パスの長さで昇順ソート（_2.csvなどのサフィックスがない元ファイルを優先）
  const sortedEntries = [...entries].sort((a, b) => {
    if (a.path.length !== b.path.length) return a.path.length - b.path.length;
    return a.path.localeCompare(b.path);
  });

  if (sortedEntries.length === 1) return sortedEntries[0];

  // 色優先度でスコアリング
  const scoreEntry = (entry: CSVIndexEntry): number => {
    const colorUpper = entry.color.toUpperCase();
    if (colorUpper === 'BLACK') return 100;
    if (colorUpper === 'WHITE') return 50;
    return 0;
  };

  let best = sortedEntries[0];
  let bestScore = scoreEntry(best);

  for (let i = 1; i < sortedEntries.length; i++) {
    const score = scoreEntry(sortedEntries[i]);
    if (score > bestScore) {
      bestScore = score;
      best = sortedEntries[i];
    }
  }

  return best;
}

/**
 * pathからメタ情報を抽出
 * 例：/bending/csv/ELEGOO_PLA_Black_XY.csv → { maker: "ELEGOO", type: "PLA", color: "Black", orientation: "XY" }
 */
function parsePathMetadata(path: string): { maker: string; type: string; color: string; orientation: string } | null {
  try {
    // ファイル名を取得
    const filename = path.split('/').pop() || '';
    // .csv拡張子を除去
    const basename = filename.replace(/\.csv$/i, '');
    // アンダースコアで分割
    const tokens = basename.split('_');
    
    if (tokens.length < 4) {
      console.warn('[parsePathMetadata] tokens不足:', path, tokens);
      return null;
    }
    
    return {
      maker: tokens[0],
      type: tokens[1],
      color: tokens[2],
      orientation: tokens[3]
    };
  } catch (error) {
    console.error('[parsePathMetadata] 解析エラー:', path, error);
    return null;
  }
}

/**
 * CSV一覧インデックスをキャッシュ
 */
let csvIndexCache: CSVIndexEntry[] | null = null;
let csvIndexPromise: Promise<CSVIndexEntry[]> | null = null;

/**
 * CSV一覧インデックスを取得
 */
async function getCSVIndex(): Promise<CSVIndexEntry[]> {
  if (csvIndexCache !== null) {
    console.log('[CSV] index.json キャッシュ使用:', csvIndexCache.length, '件');
    return csvIndexCache;
  }

  if (csvIndexPromise !== null) {
    return csvIndexPromise;
  }

  csvIndexPromise = (async () => {
    try {
      const response = await fetch('/bending/csv/index.json');
      if (!response.ok) {
        console.warn('[CSV] ✗ index.json が取得できませんでした (status:', response.status, ')');
        csvIndexPromise = null;
        return [];
      }
      const data = await response.json();
      csvIndexCache = data;
      console.log('[CSV] ✓ index.json 取得成功:', data.length, '件');
      if (data.length > 0) {
        console.log('[CSV] index.json エントリ一覧:', data.map((e: CSVIndexEntry) => `${e.maker} ${e.type} ${e.color} ${e.orientation}`));
      }
      return data;
    } catch (error) {
      console.error('[CSV] ✗ index.json の読み込みエラー:', error);
      csvIndexPromise = null;
      return [];
    } finally {
      if (csvIndexCache !== null) {
        csvIndexPromise = null;
      }
    }
  })();

  return csvIndexPromise;
}

/**
 * フィラメント種別に対して最適なCSVを選択（要素ベース完全一致）
 * 優先順位：
 * 1. maker: ELEGOO 最優先、他は同順位
 * 2. color: Black > White > その他
 * 3. pattern: pattern1 > pattern3 > pattern2
 */
function selectBestCSV(filamentType: string, csvIndex: CSVIndexEntry[], preferredMaker?: string, preferredColor?: string, preferredPattern?: string): CSVIndexEntry | null {
  console.log('[selectBestCSV] 検索開始:', { 
    filamentType, 
    preferredMaker, 
    preferredColor, 
    preferredPattern,
    indexSize: csvIndex.length 
  });
  
  if (csvIndex.length === 0) {
    console.log('[selectBestCSV] ✗ index.json が空です（CSVファイルが存在しません）');
    return null;
  }
  
  const requestedType = filamentType.trim().toUpperCase();
  
  // 1. pathから要素を抽出して、type完全一致で候補を絞り込み
  let candidates: Array<CSVIndexEntry & { parsedMeta: NonNullable<ReturnType<typeof parsePathMetadata>> }> = [];
  
  for (const entry of csvIndex) {
    const parsedMeta = parsePathMetadata(entry.path);
    if (parsedMeta) {
      const parsedType = parsedMeta.type.trim().toUpperCase();
      // type完全一致のみ候補にする（部分一致・ベース材料化は禁止）
      if (parsedType === requestedType) {
        candidates.push({ ...entry, parsedMeta });
      }
    }
  }
  
  console.log(`[selectBestCSV] type="${requestedType}" 完全一致候補: ${candidates.length} 件`);
  if (candidates.length > 0) {
    console.log('[selectBestCSV] 候補一覧:', candidates.map(c => `${c.parsedMeta.maker} ${c.parsedMeta.type} ${c.parsedMeta.color} ${c.parsedMeta.orientation} -> ${c.path}`));
  }

  if (candidates.length === 0) {
    console.log('[selectBestCSV] ✗ 候補が0件です（該当するCSVファイルが存在しません）');
    return null;
  }

  // 2. 優先条件でフィルタリング（完全一致があれば優先）
  if (preferredMaker) {
    const makerMatch = candidates.filter(c => c.parsedMeta.maker.toUpperCase() === preferredMaker.toUpperCase());
    if (makerMatch.length > 0) {
      candidates = makerMatch;
      console.log(`[CSV] メーカー "${preferredMaker}" で絞り込み:`, candidates.length, '件');
    }
  }

  if (preferredColor) {
    const colorMatch = candidates.filter(c => c.parsedMeta.color.toUpperCase() === preferredColor.toUpperCase());
    if (colorMatch.length > 0) {
      candidates = colorMatch;
      console.log(`[CSV] 色 "${preferredColor}" で絞り込み:`, candidates.length, '件');
    }
  }

  if (preferredPattern) {
    const patternMatch = candidates.filter(c => c.parsedMeta.orientation.toLowerCase() === preferredPattern.toLowerCase());
    if (patternMatch.length > 0) {
      candidates = patternMatch;
      console.log(`[CSV] パターン "${preferredPattern}" で絞り込み:`, candidates.length, '件');
    }
  }

  // 3. スコアリング関数（parsedMetaを使用）
  const scoreEntry = (entry: typeof candidates[0]): number => {
    let score = 0;
    const meta = entry.parsedMeta;

    // メーカー優先度（ELEGOO = 1000点、他 = 0点）
    if (meta.maker.toUpperCase() === 'ELEGOO') {
      score += 1000;
    }

    // 色優先度（Black = 100点、White = 50点、その他 = 0点）
    const colorUpper = meta.color.toUpperCase();
    if (colorUpper === 'BLACK') {
      score += 100;
    } else if (colorUpper === 'WHITE') {
      score += 50;
    }

    // パターン優先度（pattern1 = 10点、pattern3 = 5点、pattern2 = 1点）
    const patternLower = meta.orientation.toLowerCase();
    if (patternLower === 'pattern1') {
      score += 10;
    } else if (patternLower === 'pattern3') {
      score += 5;
    } else if (patternLower === 'pattern2') {
      score += 1;
    }

    return score;
  };

  // 4. 最高スコアのエントリを選択
  let bestEntry = candidates[0];
  let bestScore = scoreEntry(bestEntry);

  for (let i = 1; i < candidates.length; i++) {
    const score = scoreEntry(candidates[i]);
    if (score > bestScore) {
      bestScore = score;
      bestEntry = candidates[i];
    }
  }

  console.log(`[CSV] 最適候補を選択: ${bestEntry.parsedMeta.maker} ${bestEntry.parsedMeta.type} ${bestEntry.parsedMeta.color} ${bestEntry.parsedMeta.orientation} -> ${bestEntry.path} (スコア: ${bestScore})`);
  
  // parsedMetaをCSVIndexEntryに反映して返す
  const result: CSVIndexEntry = {
    maker: bestEntry.parsedMeta.maker,
    type: bestEntry.parsedMeta.type,
    color: bestEntry.parsedMeta.color,
    orientation: bestEntry.parsedMeta.orientation,
    path: bestEntry.path
  };
  
  return result;
}

/**
 * CSVテキストをパースして stress-strain データに変換
 * 新形式（strain_pct/stress_mpa列あり）を優先し、旧形式（disp/force計算）にフォールバック
 * @param csvText CSV文字列
 * @param filamentId フィラメントID
 * @param filamentName フィラメント名
 * @returns FilamentTestData または null（パース失敗時）
 */
export function parseStressStrainCSV(
  csvText: string,
  filamentId: string,
  filamentName: string
): FilamentTestData | null {
  try {
    const lines = csvText.split('\n').map(line => line.trim()).filter(line => line);
    
    // メタデータ抽出
    const metadata: { span_mm?: number; width_mm?: number; thickness_mm?: number } = {};
    let dataStartIndex = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // メタ行（# で始まる）
      if (line.startsWith('#')) {
        // スパン L の抽出（L, span, L_mm などに対応）
        const spanMatch = line.match(/(?:L|span|L_mm)[:\s=]+([0-9.]+)/i);
        if (spanMatch) {
          metadata.span_mm = parseFloat(spanMatch[1]);
        }
        
        // 幅 b の抽出（b, width, b_mm などに対応）
        const widthMatch = line.match(/(?:b|width|b_mm)[:\s=]+([0-9.]+)/i);
        if (widthMatch) {
          metadata.width_mm = parseFloat(widthMatch[1]);
        }
        
        // 厚さ h の抽出（h, thickness, h_mm などに対応）
        const thicknessMatch = line.match(/(?:h|thickness|h_mm)[:\s=]+([0-9.]+)/i);
        if (thicknessMatch) {
          metadata.thickness_mm = parseFloat(thicknessMatch[1]);
        }
      } else {
        // データ行の開始
        dataStartIndex = i;
        break;
      }
    }
    
    // ヘッダー行を探す
    let headerIndex = dataStartIndex;
    let strainIndex = -1;
    let stressIndex = -1;
    let dispIndex = -1;
    let forceIndex = -1;
    
    for (let i = dataStartIndex; i < lines.length; i++) {
      const line = lines[i];
      if (line.startsWith('#')) continue;
      
      const headers = line.split(',').map(h => h.trim().toLowerCase());
      
      // 新形式：strain_pct / stress_mpa 列を探す
      strainIndex = headers.findIndex(h => 
        h === 'strain_pct' || h === 'strain' || h === 'strain_%'
      );
      stressIndex = headers.findIndex(h => 
        h === 'stress_mpa' || h === 'stress' || h === 'stress_mpa'
      );
      
      // 旧形式：disp_mm / force_N 列を探す
      dispIndex = headers.findIndex(h => 
        h.includes('disp') && h.includes('mm') || h === 'disp_mm' || h === 'displacement_mm'
      );
      forceIndex = headers.findIndex(h => 
        h.includes('force') && h.includes('n') || h === 'force_n' || h === 'load_n'
      );
      
      if ((strainIndex >= 0 && stressIndex >= 0) || (dispIndex >= 0 && forceIndex >= 0)) {
        headerIndex = i;
        break;
      }
    }
    
    // 新形式を優先
    const useNewFormat = strainIndex >= 0 && stressIndex >= 0;
    
    if (!useNewFormat) {
      // 旧形式の場合は必須メタデータをチェック
      if (dispIndex < 0 || forceIndex < 0) {
        console.warn(`[${filamentId}] 必須列が見つかりません`);
        return null;
      }
      
      if (!metadata.span_mm || !metadata.width_mm || !metadata.thickness_mm) {
        console.warn(`[${filamentId}] 旧形式で必須メタデータが不足しています:`, metadata);
        return null;
      }
    }
    
    // データ行をパース
    const dataPoints: StressStrainPoint[] = [];
    
    if (useNewFormat) {
      // 新形式：strain_pct と stress_mpa をそのまま使用
      for (let i = headerIndex + 1; i < lines.length; i++) {
        const line = lines[i];
        if (line.startsWith('#') || !line) continue;
        
        const values = line.split(',').map(v => v.trim());
        
        const strain_pct = parseFloat(values[strainIndex]);
        const stress_mpa = parseFloat(values[stressIndex]);
        
        if (isNaN(strain_pct) || isNaN(stress_mpa)) continue;
        
        dataPoints.push({
          strain: strain_pct,
          stress: stress_mpa
        });
      }
    } else {
      // 旧形式：disp_mm と force_N から計算
      const L = metadata.span_mm!;
      const b = metadata.width_mm!;
      const h = metadata.thickness_mm!;
      
      for (let i = headerIndex + 1; i < lines.length; i++) {
        const line = lines[i];
        if (line.startsWith('#') || !line) continue;
        
        const values = line.split(',').map(v => v.trim());
        
        const disp_mm = parseFloat(values[dispIndex]);
        const force_N = parseFloat(values[forceIndex]);
        
        if (isNaN(disp_mm) || isNaN(force_N)) continue;
        
        // stress-strain 計算
        // 応力: sigma[MPa] = 3*F*L / (2*b*h^2)
        const stress = (3 * force_N * L) / (2 * b * h * h);
        
        // ひずみ: eps[-] = 6*s*h / (L^2)
        const strain = (6 * disp_mm * h) / (L * L);
        
        // ひずみを % に変換
        const strainPercent = strain * 100;
        
        dataPoints.push({
          strain: strainPercent,
          stress: stress
        });
      }
    }
    
    if (dataPoints.length === 0) {
      console.warn(`[${filamentId}] データポイントが取得できませんでした`);
      return null;
    }
    
    return {
      filamentId,
      filamentName,
      data: dataPoints,
      metadata
    };
    
  } catch (error) {
    console.error(`[${filamentId}] CSVパースエラー:`, error);
    return null;
  }
}

/**
 * CSV URLからデータを取得してパース(CSV統一版)
 * @param filamentType フィラメント種別(PLA, PETG等)
 * @param maker メーカー名(オプション)
 * @param color 色(オプション)
 * @param pattern パターン(pattern1 / pattern2 / pattern3、オプション)
 * @param csvPath 直接指定のCSVパス(オプション)
 * @returns FilamentTestData または null
 */
export async function fetchStressStrainData(
  filamentType: string,
  maker?: string,
  color?: string,
  pattern?: string,
  csvPath?: string
): Promise<FilamentTestData | null> {
  console.log('[fetchStressStrainData] 開始:', { filamentType, maker, color, pattern, csvPath });

  // CSV直リンクで取得する関数
  const fetchCSVDirect = async (path: string): Promise<string | null> => {
    try {
      console.log('[fetchStressStrainData] 直リンクで取得:', path);
      const response = await fetch(path);
      if (response.ok) {
        return await response.text();
      }
      console.warn('[fetchStressStrainData] 取得失敗:', path, response.status);
      return null;
    } catch (err) {
      console.error('[fetchStressStrainData] 取得エラー:', path, err);
      return null;
    }
  };

  // 1) 直接パスが指定されている場合
  if (csvPath) {
    console.log('[fetchStressStrainData] 直接パス指定:', csvPath);
    const text = await fetchCSVDirect(csvPath);
    if (text) {
      const parsed = parseStressStrainCSV(text, filamentType, filamentType);
      if (parsed) {
        console.log('[fetchStressStrainData] ✓ 直接パス取得成功:', parsed.data.length, 'points');
        return parsed;
      }
    }
  }

  // 2) index.json から最適な候補を選択
  console.log('[fetchStressStrainData] index.json探索開始');
  const csvIndex = await getCSVIndex();
  
  if (csvIndex.length > 0) {
    const bestEntry = selectBestCSV(filamentType, csvIndex, maker, color, pattern);
    
    if (bestEntry) {
      console.log('[fetchStressStrainData] 最適候補:', bestEntry.path);
      const text = await fetchCSVDirect(bestEntry.path);
      if (text) {
        const parsed = parseStressStrainCSV(text, filamentType, filamentType);
        if (parsed) {
          console.log('[fetchStressStrainData] ✓ index.json候補取得成功:', bestEntry.path, parsed.data.length, 'points');
          // メタ情報を追加
          parsed.maker = bestEntry.maker;
          parsed.color = bestEntry.color;
          parsed.orientation = bestEntry.orientation;
          return parsed;
        }
      }
    } else {
      console.log('[fetchStressStrainData] ✗ CSV取得失敗: データが見つかりません');
    }
  }

  console.error('[fetchStressStrainData] ✗ 全候補失敗: データが見つかりません');
  return null;
}
